import { useEnvironmentInterface } from "environment-interface";
import type { RegisterEditorParams } from "environment-interface/monacoEditor/browser/services/editor/vscode/tools/custom-editor";
import { Box } from "prism-react";
import React from "react";
import { createPortal } from "react-dom";
import type { Uri } from "vscode";

export type ComponentProps = {
  resource?: Uri;
};

export type CustomEditorProps = {
  globPattern?: string;
  paneLabel?: string;
  id: string;
  name: string;
  Component: React.FC<ComponentProps>;
  getName?: (resource?: Uri) => string;
  getIcon?: (resource?: Uri) => string | undefined;
  disablePadding?: boolean;
  serializer?: RegisterEditorParams["serializer"];
};

type Container = {
  id: number;
  element: HTMLElement;
  filePath?: Uri;
};

export const CustomEditor = React.memo(
  ({
    globPattern,
    id,
    name,
    Component,
    paneLabel,
    getName,
    disablePadding,
    getIcon,
    serializer,
  }: CustomEditorProps) => {
    const [containers, setContainers] = React.useState<Container[]>([]);
    const { monacoEditor } = useEnvironmentInterface();
    const lastId = React.useRef(0);

    React.useEffect(() => {
      const disposablePromise = monacoEditor.registerEditor({
        id,
        name,
        globPattern,
        serializer,
        getIcon,
        paneLabel,
        onRender: (element, input) => {
          const id = lastId.current++;

          setContainers((containers) => [
            ...containers,
            {
              id,
              element,
              filePath: input.resource,
            },
          ]);

          if (
            "setName" in input &&
            typeof input.setName === "function" &&
            getName
          ) {
            // This is a hacky way to set the title to the filename, but it's the
            // best way right now to set it. Ideally we use `EditorResourceAccessor`.

            input.setName(getName(input.resource));
          }

          return {
            dispose: () => {
              setContainers((containers) =>
                containers.filter((container) => container.id !== id),
              );
            },
            setInput: async (editorInput) => {
              if (
                "setName" in editorInput &&
                typeof editorInput.setName === "function" &&
                getName
              ) {
                // This is a hacky way to set the title to the filename, but it's the
                // best way right now to set it. Ideally we use `EditorResourceAccessor`.
                editorInput.setName(getName(editorInput.resource));
              }

              setContainers((containers) =>
                containers.map((container) =>
                  container.id === id
                    ? { ...container, filePath: editorInput.resource }
                    : container,
                ),
              );
            },
          };
        },
      });

      return () => {
        disposablePromise.then((disposable) => disposable.dispose());
      };
    }, []);

    return containers.map((container) =>
      createPortal(
        <Box
          key={container.id}
          css={{
            display: "block",
            width: "100%",
            padding: disablePadding ? "0" : "$2",

            boxSizing: "border-box !important",
            "*": {
              boxSizing: "border-box !important",
            },
          }}
        >
          <Component resource={container.filePath} />
        </Box>,
        container.element,
      ),
    );
  },
);
